import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/services/notification.service';
import type { NotificationCount } from '@/types/notification.types';

const QK = {
  unread: ['notifications', 'unread'],
  count: ['notifications', 'count'],
  list: ['notifications', 'list'],
} as const;

/**
 * Sprint 4.D — Compteur de notifications non lues (polling 30s).
 * Cadence cohérente avec le pattern projet (pas de SSE/WebSocket en V1).
 */
export function useNotificationCount() {
  return useQuery<NotificationCount>({
    queryKey: QK.count,
    queryFn: () => notificationService.getCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Liste les notifs non lues (chargée à l'ouverture du popover). */
export function useUnreadNotifications(enabled: boolean = true) {
  return useQuery({
    queryKey: QK.unread,
    queryFn: () => notificationService.listUnread(),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationService.markAsRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
